import { useEffect, useId, useMemo, useState } from 'react';
import {
  Box,
  Button,
  FormField,
  FormHelp,
  Input,
  Label,
  LabeledControl,
  Paragraph,
  Select,
  Switch,
} from '@zillow/constellation-10';

import HeroAnchor from '../HeroAnchor';
import { parseQueryString } from '@zg-rentals/string-utils';

import { useGetUser } from '../../hooks/queries/user';
import { useGetAddress } from '../../hooks/queries/address';
import { useGoToAddressItem, useSuggestions } from '../PriceMyRentalSearchModule/hooks';
import { useLocation } from '../../hooks/useLocation';

import PostingActions from '../../flux/actions/PostingActions';

import routingUtils from '../../utils/routingUtils';
import { paths as zrmPaths } from '../../utils/zrmPathsManifest';

import Autocomplete from '../Autocomplete';

import { useLocationRestriction, useValidateForm } from './hooks';

import { PropertyConstants } from '../../constants/PropertyConstants';

// Styles
import { rowStyles, sharedPropertyContainerStyles } from './styles';

// Types
import type { ChangeEvent } from 'react';
import type { Paths } from '@zg-rentals/paths-manifest';
import type AutocompleteAddress from '../../models/AutocompleteAddress';
import type { PropertyTypesValues } from '../../constants/PropertyConstants';
import { emitGetStartedEvent, emitListingCreatedEvent } from '../../analytics/postAListingSearchModuleEvents';
import { emitAddPropertyEvent } from '../../analytics/gtmEvents';
import type { ZrmClickstreamListing } from '@zg-rentals/clickstream';

const {
  PROPERTY_CODES_BY_TYPE,
  PROPERTY_TYPES: { COMMUNITY, CONDO, HOUSE, TOWNHOUSE },
  LISTING_TYPES: { RENTAL, ROOM },
} = PropertyConstants;

const getUrlZRMLoginPageWithRedirectParam = (
  userFormSubQueryObj: {
    address: string;
    isRoomForRent: string;
    numUnits: string;
    propertyType: string;
    unit: string;
    submitted?: string;
  },
  currentPathname: string,
  paths: Paths,
) => {
  const urlSearchString = new URLSearchParams(userFormSubQueryObj).toString();
  const userFormSubQueryString = urlSearchString ? `?${urlSearchString}` : '';
  const zrmSignInPageQueryString = `${currentPathname}${userFormSubQueryString}`;

  return paths.zrmSignInPage(zrmSignInPageQueryString) || '';
};

const getInitialPropertyType = (propertyType: PropertyTypesValues) => {
  return PROPERTY_CODES_BY_TYPE[propertyType] ? propertyType : '';
};

const PostAListingSearchModule = () => {
  const location = useLocation();

  const urlQueryObject = parseQueryString(location.search);

  const [address, updateAddress] = useState(urlQueryObject?.address || '');
  const addressInputId = useId();
  const [errorObj, updateErrorObj] = useState<{
    address?: string | JSX.Element;
    propertyType?: string | JSX.Element;
    numberOfUnits?: string;
  }>({
    address: '',
    propertyType: '',
    numberOfUnits: '',
  });
  const [unit, updateUnit] = useState(urlQueryObject?.unit || '');
  const [numUnits, updateNumUnits] = useState(urlQueryObject?.numUnits || '');

  const [userSelectedPropertyType, updateUserSelectedPropertyType] = useState(
    useMemo(() => getInitialPropertyType(urlQueryObject?.propertyType as PropertyTypesValues), [urlQueryObject]),
  );

  const [isRoomForRent, updateIsRoomForRent] = useState(urlQueryObject?.isRoomForRent === 'true');
  const [duplicateAccountListingId, updateDuplicateAccountListingId] = useState('');
  const [isLoading, updateIsLoading] = useState(urlQueryObject?.submitted === 'true' || false);

  // custom hooks
  const { data: user } = useGetUser();
  const { loggedIn: isLoggedIn, isInitialMultiFactorAuthenticationRequired } = user || {};
  const needsAuth = !isLoggedIn || isInitialMultiFactorAuthenticationRequired;
  const isPartiallyAuthenticated = isLoggedIn || isInitialMultiFactorAuthenticationRequired;

  const { setAddressDetails } = useGetAddress();

  const { updateAddressError } = useGoToAddressItem();
  const { suggestions, getSuggestions } = useSuggestions([]);
  const { checkLocationRestriction, propertyTypeFromApi } = useLocationRestriction({
    isLoggedIn,
    updateDuplicateAccountListingId,
    updateErrorObj,
    updateIsLoading,
    setAddressDetails,
  });
  const { checkIsFormComplete, verifyAddress, verifyNumUnits } = useValidateForm(updateErrorObj);

  let propertyType = userSelectedPropertyType || propertyTypeFromApi;
  if (propertyTypeFromApi === COMMUNITY) {
    propertyType = COMMUNITY;
  }
  const isCommunityType = propertyType === COMMUNITY;
  const isCondoType = propertyType === CONDO;

  // NOTE: createListing callback after submission
  useEffect(() => {
    if (urlQueryObject?.submitted && !needsAuth) {
      createListing();
    } else if (urlQueryObject?.submitted && isInitialMultiFactorAuthenticationRequired) {
      const redirectUrl = getUrlZRMLoginPageWithRedirectParam(
        { address, isRoomForRent: String(isRoomForRent), numUnits, propertyType, unit, submitted: 'true' },
        location.pathname,
        zrmPaths,
      );

      routingUtils.push(`${redirectUrl}${redirectUrl.includes('?') ? '&' : '?'}mfaStepUp=true`);
    } else {
      updateIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified for staleness
  }, [isLoggedIn, isInitialMultiFactorAuthenticationRequired]);

  const handlePropertyTypeSelect = (e: ChangeEvent<HTMLSelectElement>) => {
    const selectedPropertyType = e.target.value;
    updateUserSelectedPropertyType(selectedPropertyType);

    checkLocationRestriction({
      address,
      unit,
      isRoomForRent,
      propertyType: selectedPropertyType,
      numUnitsError: errorObj.numberOfUnits,
    });
  };

  const handleUnitChange = (e: ChangeEvent<HTMLInputElement>) => {
    const enteredUnit = e.target.value;

    updateUnit(enteredUnit);
    verifyAddress({ address, unit: enteredUnit, errorObj });

    checkLocationRestriction({
      address,
      unit: enteredUnit,
      isRoomForRent,
      propertyType,
    });
  };

  const handleNumUnitsChange = (e: ChangeEvent<HTMLInputElement>) => {
    const enteredNumUnits = e.target.value;

    updateNumUnits(enteredNumUnits);

    verifyNumUnits({ numUnits: enteredNumUnits, errorObj });
  };

  const toggleSharedListing = () => {
    updateIsRoomForRent(!isRoomForRent);

    checkLocationRestriction({
      address,
      unit,
      isRoomForRent: !isRoomForRent,
      propertyType,
    });
  };

  const createListing = async () => {
    updateIsLoading(true);

    // Duplicate listing exists. Redirect to existing listing rather than create a new one
    if (duplicateAccountListingId && !isRoomForRent) {
      if (window?.router) {
        window?.router?.transitionTo?.(`${zrmPaths.propertiesPage}/${duplicateAccountListingId}/listing`);
      } else {
        routingUtils.push(`/rental-manager/properties/${duplicateAccountListingId}/listing`);
      }

      return;
    }

    const listingType = !isCommunityType && isRoomForRent ? ROOM : RENTAL;

    try {
      const currentProperty = (await PostingActions.property.create({
        listingType,
        propertyType: propertyType as PropertyTypesValues,
        fullAddress: address,
        ...(!isCommunityType && { unitNumber: unit }),
        ...(isCommunityType && { numUnits: numUnits }),
      })()) as unknown as ZrmClickstreamListing;
      const { listingAlias } = currentProperty;

      emitListingCreatedEvent({ currentProperty, user });
      emitAddPropertyEvent();

      if (window?.router) {
        window?.router?.transitionTo?.(`${zrmPaths.propertiesPage}/${listingAlias}/create-listing`);
      } else {
        routingUtils.push(`/rental-manager/properties/${listingAlias}/create-listing`);
      }
    } catch (err) {
      const addressError = err?.response?.data?.error?.message || err?.message;
      if (addressError) {
        updateErrorObj({ ...errorObj, address: addressError });
      }
    } finally {
      updateIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    updateIsLoading(true);

    const isFormComplete = checkIsFormComplete({
      address,
      propertyType,
      numUnits,
      errorObj,
    });

    emitGetStartedEvent({ user });

    if (!isFormComplete) {
      updateIsLoading(false);
      return;
    }

    const isValidAddress = await verifyAddress({ address, unit, errorObj });
    if (!isValidAddress) {
      updateIsLoading(false);
      return;
    }

    if (needsAuth) {
      const redirectUrl = getUrlZRMLoginPageWithRedirectParam(
        { address, isRoomForRent: String(isRoomForRent), numUnits, propertyType, unit, submitted: 'true' },
        location.pathname,
        zrmPaths,
      );

      routingUtils.push(
        isInitialMultiFactorAuthenticationRequired
          ? `${redirectUrl}${redirectUrl.includes('?') ? '&' : '?'}mfaStepUp=true`
          : redirectUrl,
      );
    } else {
      createListing();
    }
  };

  // action handlers
  const handleAddressChange = (newAddressInputValue: string) => {
    updateAddress(newAddressInputValue);
    getSuggestions(newAddressInputValue);
  };

  const handleAddressSelect = (addressItem: AutocompleteAddress) => {
    // update input value when selection is made
    updateAddress(addressItem.name || '');

    checkLocationRestriction({
      address: addressItem.name || '',
      unit,
      isRoomForRent,
      propertyType,
      numUnitsError: errorObj.numberOfUnits,
    });

    // reset address input error
    updateAddressError('');
  };

  return (
    <Box
      data-testid="PostAListingSearchModule"
      css={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
    >
      <Box css={rowStyles}>
        <Box css={{ display: 'flex', flexDirection: 'column', gap: '100', width: '100%' }}>
          <Label htmlFor={addressInputId}>Street address</Label>
          <Autocomplete
            items={suggestions}
            onChange={handleAddressChange}
            onSelect={handleAddressSelect}
            placeholder="Address"
            value={address}
            error={!!errorObj.address}
            inputId={addressInputId}
            description={errorObj.address ? <FormHelp error>{errorObj.address}</FormHelp> : undefined}
          />
        </Box>

        {isCondoType && (
          <FormField
            label={<Label>Unit</Label>}
            control={<Input onChange={handleUnitChange} value={unit} />}
            // oxlint-disable-next-line constellation/no-raw-spacing-values -- `maxWidth: 210px` is past the end of the numeric sizes scale, which stops at `5000` (200px)
            css={{ width: '30%', maxWidth: '210px', marginLeft: '600' }}
          />
        )}

        {isCommunityType && (
          <FormField
            error={!!errorObj.numberOfUnits}
            label={<Label>Number of units</Label>}
            control={<Input onChange={handleNumUnitsChange} value={numUnits} />}
            description={errorObj.numberOfUnits ? <FormHelp>{errorObj.numberOfUnits}</FormHelp> : undefined}
            // oxlint-disable-next-line constellation/no-raw-spacing-values -- `maxWidth: 210px` is past the end of the numeric sizes scale, which stops at `5000` (200px)
            css={{ width: '30%', maxWidth: '210px', marginLeft: '600' }}
          />
        )}
      </Box>

      <FormField
        css={{ width: '100%', paddingBlock: '300' }}
        error={!!errorObj.propertyType}
        label={<Label>Property type</Label>}
        control={
          <Select
            value={propertyType}
            onChange={handlePropertyTypeSelect}
            css={{ color: propertyType ? undefined : 'text.action.disabled' }}
          >
            <option disabled hidden value="">
              Property type
            </option>
            <option disabled={propertyTypeFromApi === COMMUNITY} value={HOUSE}>
              {HOUSE}
            </option>
            <option disabled={propertyTypeFromApi === COMMUNITY} value={CONDO}>
              {CONDO}
            </option>
            <option disabled={propertyTypeFromApi === COMMUNITY} value={TOWNHOUSE}>
              {TOWNHOUSE}
            </option>
            <option value={COMMUNITY}>{COMMUNITY}</option>
          </Select>
        }
        description={errorObj.propertyType ? <FormHelp>{errorObj.propertyType}</FormHelp> : undefined}
      />

      {!isCommunityType && (
        <LabeledControl
          label={<Label>This is a room for rent with shared living space</Label>}
          labelPosition="left"
          control={<Switch size="md" checked={isRoomForRent} onChange={toggleSharedListing} />}
          css={sharedPropertyContainerStyles}
        ></LabeledControl>
      )}

      <Button
        data-testid="PostAListingSearchModule-getStartedButton"
        tone="brand"
        emphasis="filled"
        loading={isLoading}
        onClick={handleSubmit}
      >
        Get started
      </Button>

      {!isPartiallyAuthenticated && (
        <Paragraph textStyle="body-sm" css={{ marginTop: '400' }}>
          Already have an account?{' '}
          <HeroAnchor
            href={getUrlZRMLoginPageWithRedirectParam(
              { address, isRoomForRent: String(isRoomForRent), numUnits, propertyType, unit },
              location.pathname,
              zrmPaths,
            )}
          >
            Sign in
          </HeroAnchor>
        </Paragraph>
      )}
    </Box>
  );
};

export default PostAListingSearchModule;
